const queryParams = (params) => `?${Object.keys(params).map((_key) => [_key, params[_key]].join("=")).join("&")}`;
const APP_ENDPOINT = (appKey, prodId, params) => `https://api.yotpo.com/v1/widget/${appKey}/products/${prodId}/reviews.json${queryParams(params)}`;
// new endpoint for trusted shops
const FILTER_ENDPOINT = (channel, params = {}) => {
  const queryString = queryParams({
    ...params, ignoreStatements: false
  });
  return `https://api.etrusted.com/reviews?channels=${channel}${queryString ? "&" + queryString.slice(1) : ""}`;
};

let cachedToken = {
  value: null,
  expiresAt: 0,
};

const getAccessToken = async () => {
  const currentTime = Date.now() / 1000;

  if (cachedToken.value && currentTime < cachedToken.expiresAt) {
    return cachedToken.value;
  }

  try {
    const response = await fetch("/.netlify/functions/trusted-shops-token");
    const data = await response.json();

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${JSON.stringify(data)}`);
    }

    cachedToken = {
      value: data.access_token,
      expiresAt: currentTime + (data.expires_in || 3600) - 30,
    };

    return cachedToken.value;
  } catch (error) {
    console.error("Error fetching access token:", error);
    return null;
  }
};

const getProductId = (b64ProductId) => {
  try {
    const gId = atob(b64ProductId);
    const match = gId.match("([^/]*)$");
    const productId = match ? match[0] : null;
    return productId;
  } catch (err) {
    return null;
  }

};

const getMergedReviewsForCluster = async (variants) => {
  try {
    const channel = process.env.GATSBY_TRUSTED_SHOPS_CHANNEL;
    if (!channel) {
      console.error("GATSBY_TRUSTED_SHOPS_CHANNEL is not defined in environment variables.");
      return [];
    }

    let token = await getAccessToken();
    if (!token) {
      console.error("Failed to retrieve access token.");
      return [];
    }

    const variantRequests = variants.map(async (variant) => {
      const options = {
        sku: variant?.store?.sku, count: 5000, type: "PRODUCT_REVIEW"
      };
      const url = `${FILTER_ENDPOINT(channel, options)}`;

      try {
        let response = await fetch(url, {
          method: "GET",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 401) {
          console.warn("Access token expired. Fetching a new one...");
          token = await getAccessToken();
          response = await fetch(url, {
            method: "GET",
            headers: {
              accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
        }

        if (!response.ok) {
          console.error(`Error fetching SKU for ${variant?.store?.sku}: ${response.statusText}`);
          return [];
        }

        const data = await response.json();
        return data.items
          .filter(review => review.state === "APPROVED")
          .map(review => ({
            ...review,
            cluster: variant.cluster,
          }));

      } catch (error) {
        console.error(`Error fetching SKU for ${variant?.store?.sku}:`, error);
        return [];
      }
    });

    const mergedVariants = (await Promise.all(variantRequests)).flat();

    const uniqueReviews = Array.from(
      mergedVariants.reduce((map, review) => {
        return map.set(review.id, review);
      }, new Map()).values()
    );
    return uniqueReviews;
  } catch (error) {
    console.error("Error fetching SKUs:", error);
    return [];
  }
};



const getChannelReviews = async () => {
  try {
    const channel = process.env.GATSBY_TRUSTED_SHOPS_CHANNEL;
    if (!channel) {
      console.error("Environment variable GATSBY_TRUSTED_SHOPS_CHANNEL is missing.");
      return [];
    }

    const token = await getAccessToken();
    if (!token) {
      console.error("No valid access token received.");
      return [];
    }

    const options = {
      channels: channel,
      count: 25000,
      type: "SERVICE_REVIEW",
    };

    const url = `https://api.etrusted.com/reviews?${new URLSearchParams(options).toString()}`;


    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Accept": "application/json",
        "Authorization": `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${JSON.stringify(data)}`);
    }

    const uniqueReviews = Array.from(
      (data.items || []).reduce((map, review) => {
        const uniqueKey = `${review.id || review.sku}-${review.channel || channel}`;
        return map.set(uniqueKey, review);
      }, new Map()).values()
    );

    return uniqueReviews;
  } catch (error) {
    console.error("Error fetching channel reviews:", error);
    return [];
  }
};

const getReviews = async (shopifyId, page = "1", perPage = "10", sort = "date", sortOrder = "desc", star = null) => {
  const productId = getProductId(shopifyId);
  const appKey = process.env.GATSBY_YOTPO_API;

  let options = {
    page: page,
    per_page: perPage,
    sort: sort,
    direction: sortOrder,
  };

  if (star) options = {
    ...options,
    star
  };

  const resp = await fetch(APP_ENDPOINT(appKey, productId, options)).then((res) => res.json());

  if (!resp.status || resp.status.code !== 200) return [];

  return resp.response;
};

const getReviewSummary = async (shopifyId, star = null) => {
  const productId = getProductId(shopifyId);
  const appKey = process.env.GATSBY_YOTPO_API;

  let options = {
    page: 1,
    per_page: 1,
    sort: "date",
    direction: "desc",
  };

  if (star) options = {
    ...options,
    star
  };

  const resp = await fetch(APP_ENDPOINT(appKey, productId, options)).then((res) => res.json());

  if (!resp.status || resp.status.code !== 200) return [];

  return resp?.response?.bottomline;
};


export {
  getReviews,
  getReviewSummary,
  getChannelReviews,
  getMergedReviewsForCluster
};
